/**
 * HTML escaping, in the one place that does it.
 *
 * The three live surfaces are built from `document.createElement` and
 * `textContent`, which escapes by construction and is why this did not exist
 * before. The profile page is the first thing here that builds HTML as a
 * string, and a string built from a database column is the oldest injection
 * there is: a display name is whatever somebody typed.
 *
 * Shared rather than local to the renderer so a second server-rendered page
 * cannot arrive with its own almost-correct version. `&` goes first, or the
 * escapes escape each other.
 */
export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Serialising a value for a `<script type="application/ld+json">` block, which
 * `escapeHtml` cannot do and would corrupt if it tried.
 *
 * Inside a script element the HTML parser is not looking for entities, it is
 * looking for `</script`. So `&lt;` is not an escape here, it is three
 * characters of literal text inside a JSON string that a consumer then reads
 * back as `&lt;`: running the JSON through `escapeHtml` produces markup that
 * parses, validates, and silently misquotes every clause containing an angle
 * bracket. The right tool is the JSON string escape, which the JSON grammar
 * already provides and which no HTML parser interprets.
 *
 * `<` alone closes both holes that matter (`</script>` and `<!--`), and `>` and
 * `&` go with it because escaping half a set is how the next person concludes
 * the set was considered. U+2028 and U+2029 are legal inside a JSON string and
 * illegal inside a JavaScript string literal, so they break any consumer that
 * evaluates rather than parses, and they are invisible in a diff.
 *
 * This lives beside `escapeHtml` for the reason that function already gives: so
 * a second server-rendered surface cannot arrive with its own almost-correct
 * version. Guard `AI5`.
 */
export function escapeJsonLd(value: unknown): string {
  return JSON.stringify(value, null, 2)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');
}
